import type { AccumUpload } from '@shared/api';
import { Badge } from '@shared/ui/Badge';
import { Button } from '@shared/ui/Button';
import { fmtMoney, fmtNum } from '@shared/lib/smetaLabels';
import {
  kindLabel,
  statusLabel,
  statusTone,
  summaryFailedSources,
  summaryMissing,
  summaryNum,
  summaryTotalFiles,
  summaryWarnings,
} from './accumulationLabels';
import { Muted, MutedMargin, Table, ThRight, UploadErrorText } from './styled';

interface UploadsTableProps {
  uploads: AccumUpload[];
  canWrite: boolean;
  onDelete: (id: string) => void;
}

export function UploadsTable({ uploads, canWrite, onDelete }: UploadsTableProps) {
  if (uploads.length === 0) {
    return <MutedMargin>Загрузок ещё не было.</MutedMargin>;
  }
  return (
    <Table>
      <thead>
        <tr>
          <th>Тип</th>
          <th>Месяц</th>
          <th>Дата док.</th>
          <th>№ КС-3</th>
          <th>Статус</th>
          <ThRight>Строк</ThRight>
          <ThRight>Σ объём</ThRight>
          <ThRight>Σ стоимость</ThRight>
          <th>Без цены</th>
          <th>Дата</th>
          <th />
        </tr>
      </thead>
      <tbody>
        {uploads.map((u) => {
          const missing = summaryMissing(u);
          const warnings = summaryWarnings(u);
          const failedSources = summaryFailedSources(u);
          const totalFiles = summaryTotalFiles(u);
          return (
            <tr key={u.id}>
              <td>{kindLabel(u.kind)}</td>
              <td>{u.period_month ?? '—'}</td>
              <td>{u.doc_date ? new Date(u.doc_date).toLocaleDateString('ru-RU') : '—'}</td>
              <td>{u.ks3_no ?? '—'}</td>
              <td>
                <Badge $tone={statusTone(u.status)}>{statusLabel(u.status)}</Badge>
                {u.status === 'processing' && <Muted>{u.progress_percent}%</Muted>}
                {failedSources.length > 0 && (
                  <>
                    {' '}
                    <Badge $tone="warning" title={failedSources.join('\n')}>
                      не разобрано {failedSources.length} из {totalFiles}
                    </Badge>
                  </>
                )}
                {warnings.length > 0 && (
                  <>
                    {' '}
                    <Badge $tone="warning" title={warnings.join('\n')}>
                      формулы
                    </Badge>
                  </>
                )}
                {u.status === 'failed' && u.error_message && (
                  <UploadErrorText>{u.error_message}</UploadErrorText>
                )}
              </td>
              <td className="num">{u.entry_count}</td>
              <td className="num">{fmtNum(summaryNum(u, 'qty_total'))}</td>
              <td className="num">{fmtMoney(summaryNum(u, 'cost_total'))}</td>
              <td>{missing.length > 0 ? <Badge $tone="warning">{missing.length}</Badge> : '—'}</td>
              <td>{new Date(u.created_at).toLocaleDateString('ru-RU')}</td>
              <td>
                {canWrite && u.status !== 'processing' && (
                  <Button
                    $variant="ghost"
                    $size="sm"
                    type="button"
                    title="Откатить загрузку (удалить дописанные строки)"
                    onClick={() => onDelete(u.id)}
                  >
                    Откатить
                  </Button>
                )}
              </td>
            </tr>
          );
        })}
      </tbody>
    </Table>
  );
}
